import type { Session } from '@supabase/supabase-js';
import type { ReactNode } from 'react';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../services/supabaseClient';

// Types pour l'utilisateur avec rôle
interface UserProfile {
  id: string;
  email: string;
  nom: string;
  prenom: string;
  role: 'admin' | 'gerant' | 'vendeur' | 'magasin';
  created_at: string;
  updated_at: string;
}

// Type pour la réponse RPC (non utilisé actuellement)
// interface UserProfileRPC {
//   id: string;
//   email: string;
//   nom: string;
//   prenom: string;
//   role_nom: 'admin' | 'gerant' | 'vendeur';
//   auth_user_id: string;
//   created_at: string;
//   updated_at: string;
// }

interface AuthContextType {
  user: UserProfile | null;
  session: Session | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [rpcFailed, setRpcFailed] = useState(false); // Tracker si l'appel RPC échoue systématiquement

  // Fonction pour charger le profil utilisateur via RPC sécurisé
  const loadUserProfile = async (authUserId: string): Promise<UserProfile | null> => {
    try {
      // Si RPC a déjà échoué, ne pas réessayer
      if (rpcFailed) {
        return null;
      }

      // Appel RPC simple sans timeout - s'exécute en arrière-plan
      const { data, error } = await supabase
        .rpc('get_user_profile_by_auth', { p_auth_user_id: authUserId })
        .single();

      if (error) {
        setRpcFailed(true);
        return null;
      }

      if (!data) {
        setRpcFailed(true); // Marquer l'appel RPC comme défaillant

        return null;
      }

      // Utilisation directe des champs RPC sans parsing
      const rpcData = data as any; // Utiliser any pour gérer les variations de nom de colonne
      const userProfile: UserProfile = {
        id: rpcData.id,
        email: rpcData.email,
        nom: rpcData.nom,
        prenom: rpcData.prenom,
        role: rpcData.role_nom || rpcData.role || 'vendeur', // Gérer les deux noms de colonne possibles
        created_at: rpcData.created_at,
        updated_at: rpcData.updated_at,
      };

      return userProfile;
    } catch (error) {
      setRpcFailed(true); // Marquer l'appel RPC comme défaillant

      return null;
    }
  };

  // Nouvelle fonction pour charger le profil en arrière-plan
  const loadUserProfileBackground = async (authUserId: string, currentUser: UserProfile) => {
    try {
      const { data, error } = await supabase
        .rpc('get_user_profile_by_auth', { p_auth_user_id: authUserId })
        .single();

      if (data && !error) {
        const rpcData = data as any; // Utiliser any pour gérer les variations de nom de colonne
        const updatedProfile: UserProfile = {
          id: rpcData.id,
          email: rpcData.email,
          nom: rpcData.nom,
          prenom: rpcData.prenom,
          role: rpcData.role_nom || rpcData.role || 'vendeur', // Gérer les deux noms de colonne possibles
          created_at: rpcData.created_at,
          updated_at: rpcData.updated_at,
        };

        // Mettre à jour le profil seulement si les données sont différentes
        if (JSON.stringify(updatedProfile) !== JSON.stringify(currentUser)) {
          setUser(updatedProfile);
        }
      } else {
      }
    } catch (error) {
      // Erreur ignorée
    }
  };

  // Fonction pour créer un utilisateur temporaire
  const createTempUser = (authSession: Session): UserProfile => {
    const authUser = authSession.user;

    // Extraire le nom et prénom du metadata ou de l'email
    const metadata = authUser.user_metadata || {};
    const email = authUser.email || '';

    // Tenter d'extraire nom et prénom du metadata d'abord
    let nom = metadata.nom || metadata.last_name || '';
    let prenom = metadata.prenom || metadata.first_name || '';

    // Si pas de nom/prénom dans metadata, utiliser l'email
    if (!nom && !prenom && email) {
      const emailPart = email.split('@')[0];
      prenom = emailPart.charAt(0).toUpperCase() + emailPart.slice(1);
      nom = 'Utilisateur';
    }

    // Valeurs par défaut
    if (!prenom) prenom = 'Utilisateur';
    if (!nom) nom = 'Luxori';

    return {
      id: authUser.id,
      email: email,
      nom: nom,
      prenom: prenom,
      role: 'vendeur', // Rôle par défaut
      created_at: authUser.created_at || new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
  };

  // Fonction de connexion
  const login = async (email: string, password: string): Promise<void> => {
    setIsLoading(true);
    try {
      // Nettoyer les données d'entrée
      const cleanEmail = email.trim().normalize('NFC');
      const cleanPassword = password.trim().normalize('NFC');

      const { data, error } = await supabase.auth.signInWithPassword({
        email: cleanEmail,
        password: cleanPassword,
      });

      if (error) {
        throw new Error(error.message);
      }

      // Vérifier que la session a bien été créée
      if (!data.session) {
        throw new Error('Session non créée après connexion');
      }

      // La session sera traitée par onAuthStateChange, mais on met un timeout de sécurité réduit
      setTimeout(() => {
        if (isLoading) {
          setIsLoading(false);
        }
      }, 3000); // 3 secondes maximum car profil temporaire immédiat
    } catch (error) {
      setIsLoading(false);
      throw error;
    }
  };

  // Fonction de déconnexion
  const logout = async (): Promise<void> => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) {
      }
      // Le nettoyage sera fait par onAuthStateChange
    } catch (error) {}
  };

  // Initialisation unique au démarrage
  useEffect(() => {
    let mounted = true;

    // Timeout de sécurité global réduit
    const globalTimeout = setTimeout(() => {
      if (mounted && isLoading) {
        setIsLoading(false);
      }
    }, 500); // Réduit de 2s à 500ms pour un chargement ultra-rapide

    // Fonction d'initialisation centralisée
    const initializeAuth = async () => {
      try {
        // 1. Récupérer la session actuelle
        const {
          data: { session },
          error,
        } = await supabase.auth.getSession();

        if (error) {
          setIsLoading(false);
          return;
        }

        if (session && mounted) {
          setSession(session);

          // STRATÉGIE ULTRA-RAPIDE : Profil temporaire IMMÉDIAT sans RPC
          const tempUser = createTempUser(session);
          setUser(tempUser);
          setIsLoading(false); // Débloquer IMMÉDIATEMENT

          // RPC complètement désactivé pour éviter tout délai
          // L'utilisateur peut utiliser l'application avec le profil temporaire
        } else {
          if (mounted) {
            setIsLoading(false);
          }
        }
      } catch (error) {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    // Lancer l'initialisation
    initializeAuth();

    // Écouter les changements d'authentification
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!mounted) return;

      if (event === 'SIGNED_IN' && session) {
        setSession(session);

        // STRATÉGIE RAPIDE : Profil temporaire IMMÉDIAT
        const tempUser = createTempUser(session);
        setUser(tempUser);

        setIsLoading(false); // Débloquer l'interface IMMÉDIATEMENT

        // Tentative RPC en arrière-plan (VRAIMENT non bloquante - pas d'await)
        loadUserProfile(session.user.id)
          .then(userProfile => {
            if (userProfile && mounted) {
              setUser(userProfile);
            } else if (mounted) {
              // Essayer de charger en arrière-plan après 2 secondes
              setTimeout(() => {
                if (mounted) {
                  loadUserProfileBackground(session.user.id, tempUser);
                }
              }, 2000);
            }
          })
          .catch(() => {
            // Erreur RPC ignorée
            // Essayer de charger en arrière-plan après 2 secondes
            setTimeout(() => {
              if (mounted) {
                loadUserProfileBackground(session.user.id, tempUser);
              }
            }, 2000);
          });
      } else if (event === 'SIGNED_OUT') {
        setSession(null);
        setUser(null);
        setIsLoading(false);
      } else if (event === 'TOKEN_REFRESHED') {
        if (session) {
          setSession(session);
          // Pas besoin de recharger le profil, juste mettre à jour la session
        }
      }
    });

    return () => {
      mounted = false;
      clearTimeout(globalTimeout);
      subscription.unsubscribe();
    };
  }, []);

  const value: AuthContextType = {
    user,
    session,
    isLoading,
    login,
    logout,
    isAuthenticated: !!user && !!session,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth doit être utilisé dans un AuthProvider');
  }
  return context;
};
